import {useSelector} from 'react-redux';
import {useMemo} from 'react';
import {DeviceType} from '../incase-domain/common/DeviceType.enum';
import {selectedPresentationSelector, selectedViewSelector} from '../store/selectors/presentation.selectors';
import {selectedSlideSelector} from '../store/selectors/slide.selectors';
import {SLIDE_COLOR_DEFAULT} from '../constants';
import {useSlideUpdateContext} from '../ui/context/SetSlideUpdateContext';

interface useSlideBackgroundReturn {
    presentationBackgroundColor: string | null | undefined;
    presentationBackgroundImage: string | undefined;
    presentationMobileBackgroundImage: string | undefined;
    slideBackgroundColor: string | null | undefined;
    slideBackgroundImage: string | undefined;
    slideMobileBackgroundImage: string | undefined;
    effectiveBackgroundColor: string;
    effectiveBackgroundImage: string | undefined;
    setSlideBackgroundColor: (color: string | null | undefined) => void;
    setSlideBackgroundImage: (image: {image: string; name: string} | null) => void;
}

export function useSlideBackground(): useSlideBackgroundReturn {
    const {setSlide} = useSlideUpdateContext();

    const selectedPresentation = useSelector(selectedPresentationSelector);
    const selectedSlide = useSelector(selectedSlideSelector);
    const selectedView = useSelector(selectedViewSelector);

    const presentationBackgroundColor = selectedPresentation?.backgroundColor;
    const presentationBackgroundImage = selectedPresentation?.presentationBackground;
    const presentationMobileBackgroundImage = selectedPresentation?.presentationMobileBackground;

    const slideBackgroundColor = selectedSlide?.backgroundColor;
    const slideBackgroundImage = selectedSlide?.slideBackground;
    const slideMobileBackgroundImage = selectedSlide?.slideMobileBackground;

    const effectiveBackgroundColor = useMemo(
        () => slideBackgroundColor || presentationBackgroundColor || SLIDE_COLOR_DEFAULT,
        [slideBackgroundColor, presentationBackgroundColor]
    );

    const effectiveBackgroundImage = useMemo(() => {
        const isDesktop = selectedView === DeviceType.DESKTOP;
        const slideImage = isDesktop ? slideBackgroundImage : slideMobileBackgroundImage;
        const presentationImage = isDesktop ? presentationBackgroundImage : presentationMobileBackgroundImage;

        if (slideBackgroundColor && !slideImage) return undefined;

        return slideImage || presentationImage;
    }, [
        slideBackgroundColor,
        selectedView,
        slideBackgroundImage,
        presentationBackgroundImage,
        slideMobileBackgroundImage,
        presentationMobileBackgroundImage,
    ]);

    function setSlideBackgroundColor(color: string | null | undefined): void {
        if (selectedSlide) {
            setSlide({...selectedSlide, backgroundColor: color || null});
        }
    }

    function setSlideBackgroundImage(image: {image: string; name: string} | null): void {
        if (selectedSlide) {
            if (selectedView === DeviceType.DESKTOP) {
                setSlide({...selectedSlide, slideBackground: image?.image || ''});
            } else if (selectedView === DeviceType.MOBILE) {
                setSlide({...selectedSlide, slideMobileBackground: image?.image || ''});
            }
        }
    }

    return {
        presentationBackgroundColor,
        presentationBackgroundImage,
        presentationMobileBackgroundImage,
        slideBackgroundColor,
        slideBackgroundImage,
        slideMobileBackgroundImage,
        effectiveBackgroundColor,
        effectiveBackgroundImage,
        setSlideBackgroundColor,
        setSlideBackgroundImage,
    };
}
